/**
 * The signed-in person, at the right of the header bar as Jobber draws it
 * (Dakota, 2026-10-05): the initial in a circle, and under it on a click the
 * menu — who this is and their roles, Profile (the email signature, design
 * 43), Sign out, the version at the bottom. Closes on a click outside, on
 * Escape, on Profile and after Sign out.
 */

import { ROLE_DESCRIPTIONS, type Role } from '@unitedos/shared';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { signOut } from '../lib/supabase.js';

/** The letter in the circle: the address's first, or "?" for an account with none. */
export function initialOf(who: string): string {
  return (who.trim()[0] ?? '?').toUpperCase();
}

export function AccountMenu({ who, roles, roleText, roleError, version }: { who: string; roles: readonly Role[]; roleText: string; roleError: boolean; version: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const initial = initialOf(who);
  return (
    <div className="account" ref={ref}>
      <button
        type="button"
        className={`topbar__icon account__button${open ? ' topbar__icon--active' : ''}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account"
        title={who}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="avatar" aria-hidden="true">
          {initial}
        </span>
      </button>
      {open && (
        <div className="account__menu" role="menu" aria-label="Account">
          <div className="account__who">
            <span className="avatar avatar--large" aria-hidden="true">
              {initial}
            </span>
            <div className="account__text">
              <div className="account__name" title={who}>
                {who}
              </div>
              <div className={`account__roles${roleError ? ' text-bad' : ''}`} title={roles.map((r) => `${r}: ${ROLE_DESCRIPTIONS[r]}`).join('\n')}>
                {roleText}
              </div>
            </div>
          </div>
          <div className="account__rule" role="separator" />
          <Link to="/profile" className="account__item" role="menuitem" title="Your email signature" onClick={() => setOpen(false)}>
            Profile
          </Link>
          <button type="button" className="account__item" role="menuitem" onClick={() => void signOut()}>
            Sign out
          </button>
          <div className="account__rule" role="separator" />
          <div className="account__version" title={version}>
            {version}
          </div>
        </div>
      )}
    </div>
  );
}
